import { useEffect, useState, useCallback, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import api from '../api';
import Layout from '../components/Layout';
import { useToast } from '../components/ToastContext';
import { T } from '../styles/theme';
import { useIsMobile } from '../hooks/useIsMobile';

// FIX 3 — Check icon component (viola gradient circle)
function CheckIcon({ size = 20 }) {
  return (
    <span style={{
      width: size, height: size, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      background: T.gradPrimary, color: 'white', fontSize: size * 0.35, fontWeight: 800, flexShrink: 0,
    }}>✓</span>
  );
}

// Empty circle for incomplete lessons
function EmptyCircle({ size = 18 }) {
  return (
    <span style={{
      width: size, height: size, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      border: `1px solid ${T.border}`, background: 'transparent', flexShrink: 0,
    }} />
  );
}

export default function Corso() {
  const [moduli, setModuli] = useState([]);
  const [lezioni, setLezioni] = useState({});
  const [selectedLezione, setSelectedLezione] = useState(null);
  const [lezioneData, setLezioneData] = useState(null);
  const [progress, setProgress] = useState({ total: 0, completed: 0, percent: 0 });
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetting, setResetting] = useState(false);
  const toast = useToast();
  const isMobile = useIsMobile();
  const [sidebarAperta, setSidebarAperta] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [hasClickedHoLetto, setHasClickedHoLetto] = useState(false);
  const [showConfermaModal, setShowConfermaModal] = useState(false);
  const lessonContentRef = useRef(null);
  const nextBtnRef = useRef(null);

  useEffect(() => {
    if (isMobile && !localStorage.getItem('corso_hint_shown')) {
      setShowHint(true);
      localStorage.setItem('corso_hint_shown', 'true');
      setTimeout(() => setShowHint(false), 4000);
    }
  }, [isMobile]);

  // Reset quando cambia lezione
  useEffect(() => {
    setHasScrolledToBottom(false);
    setHasClickedHoLetto(false);
    if (lessonContentRef.current) {
      lessonContentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [selectedLezione]);

  // IntersectionObserver sul bottone "Ho letto"
  useEffect(() => {
    if (!nextBtnRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setHasScrolledToBottom(true); },
      { threshold: 0.5 }
    );
    observer.observe(nextBtnRef.current);
    return () => observer.disconnect();
  }, [selectedLezione, lezioneData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll fallback
  useEffect(() => {
    const handleScroll = () => {
      const el = lessonContentRef.current;
      if (el) {
        if (el.scrollTop + el.clientHeight >= el.scrollHeight * 0.85) setHasScrolledToBottom(true);
      } else {
        if (window.scrollY + window.innerHeight >= document.documentElement.scrollHeight * 0.85) setHasScrolledToBottom(true);
      }
    };
    const target = lessonContentRef.current || window;
    target.addEventListener('scroll', handleScroll);
    return () => target.removeEventListener('scroll', handleScroll);
  }, [selectedLezione]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadModuli = useCallback(async () => {
    try {
      const [mRes, pRes] = await Promise.all([api.get('/corso/moduli'), api.get('/corso/progresso')]);
      setModuli(Array.isArray(mRes.data) ? mRes.data : []);
      setProgress(pRes.data || {});
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { loadModuli(); }, [loadModuli]);

  const loadLezioni = async (moduloId) => {
    if (lezioni[moduloId]) return lezioni[moduloId];
    try {
      const r = await api.get(`/corso/moduli/${moduloId}/lezioni`);
      setLezioni((prev) => ({ ...prev, [moduloId]: r.data }));
      return r.data;
    } catch { return []; }
  };

  const selectLezione = async (moduloId, lezioneId) => {
    const lz = await loadLezioni(moduloId);
    const l = lz.find((x) => x.id === lezioneId);
    if (l && !l.accessible) return;
    setSelectedLezione(lezioneId);
    if (isMobile) setSidebarAperta(false);
    setLezioneData(l ? { ...l, modulo_id: moduloId, modulo_titolo: moduli.find((m) => m.id === moduloId)?.titolo } : null);
  };

  const completaLezione = async () => {
    if (!lezioneData || lezioneData.completata || completing) return;
    if (!hasScrolledToBottom) return;
    setCompleting(true);
    try {
      await api.post(`/corso/lezioni/${lezioneData.id}/completa`);
      const [mRes, pRes] = await Promise.all([api.get('/corso/moduli'), api.get('/corso/progresso')]);
      setModuli(Array.isArray(mRes.data) ? mRes.data : []);
      setProgress(pRes.data || {});
      const lRes = await api.get(`/corso/moduli/${lezioneData.modulo_id}/lezioni`);
      setLezioni((prev) => ({ ...prev, [lezioneData.modulo_id]: lRes.data }));
      setLezioneData((prev) => ({ ...prev, completata: true }));
      setHasClickedHoLetto(true);
    } catch {} finally { setCompleting(false); }
  };

  const handleProssimaLezione = () => {
    if (!nextLezione || nextLezione.modulo_locked) return;
    selectLezione(nextLezione.modulo_id, nextLezione.id);
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      if (lessonContentRef.current) {
        lessonContentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }, 100);
  };

  const handleResetCorso = async () => {
    setResetting(true);
    try {
      await api.delete('/corso/reset');
      toast.success('Corso resettato!');
      setShowResetModal(false);
      setSelectedLezione(null);
      setLezioneData(null);
      setLezioni({});
      setLoading(true);
      loadModuli();
    } catch { toast.error('Errore reset corso'); }
    finally { setResetting(false); }
  };

  const allLezioni = moduli.flatMap((m) => (m.lezioni || []).map((l) => ({ ...l, modulo_id: m.id, modulo_locked: m.locked })));
  const currentIdx = allLezioni.findIndex((l) => l.id === selectedLezione);
  const nextLezione = currentIdx < allLezioni.length - 1 ? allLezioni[currentIdx + 1] : null;
  const prevLezione = currentIdx > 0 ? allLezioni[currentIdx - 1] : null;

  const moduloProgress = (m) => m.lesson_count > 0 ? Math.round((m.completed_count / m.lesson_count) * 100) : 0;

  if (loading) return (
    <Layout>
      <div className="text-center py-20" style={{ color: T.muted, fontFamily: T.fontBody }}>Caricamento corso...</div>
    </Layout>
  );

  return (
    <Layout>
      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', height: isMobile ? 'auto' : 'calc(100vh - 64px)', overflow: isMobile ? 'visible' : 'hidden' }}>
        {/* Sidebar */}
        <div style={{ width: isMobile ? '100%' : 300, flexShrink: 0, borderRight: isMobile ? 'none' : `1px solid ${T.border}`, borderBottom: isMobile ? `1px solid ${T.border}` : 'none', overflowY: isMobile ? 'visible' : 'auto', backgroundColor: T.bg, fontFamily: T.fontBody, padding: isMobile ? '12px 16px' : '28px 0 0' }}>

          {/* DESKTOP — header sempre visibile */}
          {!isMobile && (
            <div style={{ padding: '0 16px 16px', borderBottom: `1px solid ${T.border}` }}>
              <div className="flex items-center gap-2 mb-3">
                <span className="text-2xl">🎓</span>
                <span className="font-bold text-sm" style={{ color: T.violet, fontFamily: T.fontDisplay }}>Corso Reselling</span>
              </div>
              <div className="text-xs mb-1" style={{ color: T.muted }}>{progress.completed}/{progress.total} lezioni completate</div>
              <div className="w-full h-2 rounded-full overflow-hidden" style={{ backgroundColor: T.border }}>
                <div className="h-full rounded-full transition-all" style={{ width: `${progress.percent}%`, background: T.gradPrimary }} />
              </div>
              <div className="text-right text-xs mt-1" style={{ color: T.violet }}>{progress.percent}%</div>
            </div>
          )}

          {/* MOBILE — hint prima volta */}
          {isMobile && showHint && (
            <div style={{
              background: 'linear-gradient(135deg,#7c3aed,#4f46e5)',
              borderRadius: 12, padding: '12px 16px', marginBottom: 12,
              display: 'flex', alignItems: 'center', gap: 10,
              animation: 'fadeInUp 0.3s ease',
            }}>
              <span style={{ fontSize: '1.2rem' }}>👆</span>
              <div>
                <div style={{ color: 'white', fontWeight: 600, fontSize: '0.85rem' }}>
                  Tocca "Vedi tutti i moduli" per navigare il corso
                </div>
                <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.75rem' }}>
                  Questo messaggio appare solo la prima volta
                </div>
              </div>
            </div>
          )}

          {/* MOBILE — trigger collapsible moduli */}
          {isMobile && (
            <div
              onClick={() => setSidebarAperta(!sidebarAperta)}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                background: sidebarAperta ? 'rgba(124,58,237,0.15)' : '#1c1640',
                border: `1px solid ${sidebarAperta ? '#7c3aed' : '#2d1b69'}`,
                borderRadius: 14, padding: '14px 16px', cursor: 'pointer',
                marginBottom: 12, transition: 'all 0.2s',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: '1.2rem' }}>📚</span>
                <div>
                  <div style={{
                    fontFamily: 'Syne', fontWeight: 700, fontSize: '0.95rem',
                    color: sidebarAperta ? '#a78bfa' : '#e2e8f0',
                  }}>
                    {sidebarAperta ? 'Nascondi moduli' : '📋 Vedi tutti i moduli'}
                  </div>
                  <div style={{ fontSize: '0.72rem', color: '#6b7280', marginTop: 2 }}>
                    {sidebarAperta ? 'Tocca per chiudere' : `${moduli.length} moduli disponibili • Tocca per espandere`}
                  </div>
                </div>
              </div>
              <div style={{
                width: 32, height: 32, borderRadius: '50%',
                background: sidebarAperta ? '#7c3aed' : '#2d1b69',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.3s', transform: sidebarAperta ? 'rotate(180deg)' : 'rotate(0deg)',
                flexShrink: 0,
              }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </div>
            </div>
          )}

          {/* MOBILE — progress bar sotto trigger */}
          {isMobile && (
            <div style={{ marginBottom: sidebarAperta ? 12 : 0 }}>
              <div className="text-xs mb-1" style={{ color: T.muted }}>{progress.completed}/{progress.total} lezioni completate</div>
              <div className="w-full h-2 rounded-full overflow-hidden" style={{ backgroundColor: T.border }}>
                <div className="h-full rounded-full transition-all" style={{ width: `${progress.percent}%`, background: T.gradPrimary }} />
              </div>
              <div className="text-right text-xs mt-1" style={{ color: T.violet }}>{progress.percent}%</div>
            </div>
          )}

          {/* Corso Completato box */}
          {(!isMobile || sidebarAperta) && progress.percent === 100 && (
            <div style={{ margin: '0 8px 12px', padding: 16, background: 'linear-gradient(135deg,rgba(124,58,237,0.15),rgba(79,70,229,0.08))', border: `1px solid ${T.violetDark}`, borderRadius: 14, textAlign: 'center' }}>
              <span style={{ fontSize: '2rem' }}>🏆</span>
              <p style={{ fontFamily: T.fontBody, fontWeight: 700, color: T.violet, fontSize: '0.9rem', marginTop: 4 }}>Corso Completato!</p>
              <p style={{ fontFamily: T.fontBody, fontSize: '0.82rem', color: T.muted, marginTop: 2 }}>Vuoi ripassare dall'inizio?</p>
              <button onClick={() => setShowResetModal(true)} style={{
                background: 'transparent', border: `1px solid ${T.red}`, color: T.red, borderRadius: 9999,
                padding: '8px 16px', fontSize: '0.82rem', fontFamily: T.fontBody, cursor: 'pointer', marginTop: 10,
              }}>↺ Ricomincia da capo</button>
            </div>
          )}

          {/* Lista moduli */}
          {(!isMobile || sidebarAperta) && <div style={{ padding: '8px 8px' }}>
            {(moduli || []).map((m) => {
              const mp = moduloProgress(m);
              const isComplete = mp === 100 && m.lesson_count > 0;
              const isActive = !isComplete && !m.locked && selectedLezione && (m.lezioni || []).some((l) => l.id === selectedLezione);
              const isExpanded = lezioni[m.id] && !m.locked;

              let moduleStyle = {};
              if (isMobile) {
                if (isComplete) {
                  moduleStyle = { background: 'rgba(124,58,237,0.12)', border: '1px solid #7c3aed', borderRadius: 10 };
                } else if (isActive) {
                  moduleStyle = { background: 'rgba(124,58,237,0.2)', border: '1px solid #a78bfa', borderRadius: 10 };
                } else {
                  moduleStyle = { background: 'transparent', border: `1px solid ${T.border}`, borderRadius: 10 };
                }
              } else {
                if (isComplete) {
                  moduleStyle = {
                    background: 'linear-gradient(135deg, rgba(124,58,237,0.2), rgba(79,70,229,0.1))',
                    border: '1px solid rgba(124,58,237,0.3)', borderLeftWidth: 3, borderLeftColor: '#7c3aed', borderRadius: 14,
                  };
                } else if (isActive) {
                  moduleStyle = {
                    background: 'rgba(124,58,237,0.12)',
                    border: '1px solid rgba(167,139,250,0.2)', borderLeftWidth: 3, borderLeftColor: '#a78bfa', borderRadius: 14,
                    boxShadow: '0 0 16px rgba(124,58,237,0.15)',
                  };
                } else {
                  moduleStyle = {
                    background: 'transparent', border: `1px solid ${T.border}`, borderLeftWidth: 3, borderLeftColor: T.border, borderRadius: 14,
                  };
                }
              }

              return (
                <div key={m.id} style={{ marginBottom: 6 }}>
                  <button
                    onClick={() => { if (!m.locked) loadLezioni(m.id); }}
                    className="w-full text-left transition-all"
                    style={{ ...moduleStyle, padding: isMobile ? '10px 12px' : '12px 14px', width: '100%', cursor: m.locked ? 'default' : 'pointer' }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {isComplete ? <CheckIcon size={20} /> : m.locked ? <span className="text-sm">🔒</span> : <span className="text-sm">📚</span>}
                        <span style={{ fontSize: isMobile ? '0.85rem' : '0.875rem', color: isComplete ? T.violet : isActive ? T.text : m.locked ? T.muted : T.text, fontWeight: isComplete || isActive ? 700 : 500 }}>{m.titolo}</span>
                      </div>
                      {m.locked && <span className="badge badge-amber text-[9px]">PRO</span>}
                    </div>
                    {!m.locked && (
                      <div className="flex items-center gap-2 mt-1.5">
                        <div className="flex-1 h-1 rounded-full" style={{ backgroundColor: 'rgba(45,31,110,0.5)' }}>
                          <div className="h-full rounded-full" style={{ width: `${mp}%`, background: T.gradPrimary }} />
                        </div>
                        <span style={{ fontSize: '0.75rem', color: isComplete ? T.violet : T.muted }}>{m.completed_count}/{m.lesson_count}</span>
                      </div>
                    )}
                  </button>

                  {/* Lezioni del modulo */}
                  {isExpanded && (lezioni[m.id] || []).map((l) => (
                    <button key={l.id}
                      onClick={() => l.accessible && selectLezione(m.id, l.id)}
                      className="w-full flex items-center gap-2.5 py-2 text-xs transition-colors"
                      style={{
                        paddingLeft: isMobile ? 20 : 28, paddingRight: 14,
                        backgroundColor: selectedLezione === l.id ? 'rgba(124,58,237,0.1)' : 'transparent',
                        borderRight: selectedLezione === l.id ? `2px solid ${T.violet}` : '2px solid transparent',
                        borderRadius: '0 8px 8px 0',
                        cursor: l.accessible ? 'pointer' : 'not-allowed',
                        opacity: l.accessible ? 1 : 0.4,
                      }}
                    >
                      {l.completata ? <CheckIcon size={18} /> : !l.accessible ? <span style={{ fontSize: '0.75rem', flexShrink: 0, width: 18, textAlign: 'center' }}>🔒</span> : <EmptyCircle size={18} />}
                      <span className="truncate" style={{
                        color: l.completata ? T.violet : !l.accessible ? '#374151' : selectedLezione === l.id ? T.text : T.muted,
                        fontWeight: l.completata ? 500 : 400,
                      }}>
                        {l.titolo}
                      </span>
                    </button>
                  ))}
                </div>
              );
            })}
          </div>}
        </div>

        {/* Content area */}
        <div ref={lessonContentRef} style={{ flex: 1, overflowY: 'auto', backgroundColor: T.bg, WebkitOverflowScrolling: 'touch' }}>
          {!lezioneData ? (
            /* Welcome screen */
            <div className="max-w-3xl mx-auto" style={{ padding: isMobile ? 16 : '28px 32px 48px' }}>
              <h1 className="page-title mb-2">Benvenuto nel Corso</h1>
              <p className="mb-8" style={{ color: T.muted, fontFamily: T.fontBody }}>Impara le tecniche di reselling passo dopo passo</p>

              <div className="grid grid-cols-3 gap-4 mb-8">
                {[
                  { label: 'Completate', value: progress.completed, color: T.violet },
                  { label: 'Moduli', value: moduli.length, color: T.text },
                  { label: 'Progresso', value: `${progress.percent}%`, color: T.violet },
                ].map((s) => (
                  <div key={s.label} className="card rounded-2xl p-4 text-center">
                    <div className="text-2xl font-bold" style={{ color: s.color, fontFamily: T.fontBody }}>{s.value}</div>
                    <div className="text-xs" style={{ color: T.muted, fontFamily: T.fontBody }}>{s.label}</div>
                  </div>
                ))}
              </div>

              {/* FIX 3 — Module cards with completed style */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {(moduli || []).map((m) => {
                  const mp = moduloProgress(m);
                  const isComplete = mp === 100 && m.lesson_count > 0;

                  const cardStyle = isComplete ? {
                    border: '1px solid rgba(124,58,237,0.4)',
                    background: 'linear-gradient(135deg, rgba(124,58,237,0.1), rgba(79,70,229,0.05))',
                  } : m.locked ? {
                    borderColor: '#92400e', opacity: 0.6,
                  } : {};

                  return (
                    <button key={m.id} onClick={() => { if (!m.locked) loadLezioni(m.id); }}
                      className="card rounded-2xl p-5 text-left transition-all hover:scale-[1.01]"
                      style={cardStyle}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          {isComplete ? <CheckIcon size={22} /> : m.locked ? <span className="text-xl">🔒</span> : <span className="text-xl">📚</span>}
                          {m.locked && <span className="badge badge-amber text-[9px]">PRO</span>}
                        </div>
                        {isComplete && (
                          <span style={{
                            background: 'rgba(124,58,237,0.15)', border: `1px solid ${T.violetDark}`, color: T.violet,
                            borderRadius: 20, padding: '3px 10px', fontSize: '0.72rem', fontWeight: 700, fontFamily: T.fontBody,
                          }}>✓ Completato</span>
                        )}
                      </div>
                      <div className="font-bold text-sm" style={{ color: isComplete ? T.violet : T.text }}>{m.titolo}</div>
                      <div className="text-xs mt-1" style={{ color: T.muted, fontFamily: T.fontBody }}>{m.descrizione}</div>
                      {!m.locked && (
                        <div className="flex items-center gap-2 mt-3">
                          <div className="flex-1 h-1.5 rounded-full" style={{ backgroundColor: T.border }}>
                            <div className="h-full rounded-full" style={{ width: `${mp}%`, background: T.gradPrimary }} />
                          </div>
                          <span className="text-xs" style={{ color: isComplete ? T.violet : T.muted }}>{m.completed_count}/{m.lesson_count}</span>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
            /* Lesson viewer */
            <div className="max-w-3xl mx-auto" style={{ padding: isMobile ? '16px' : '28px 32px 48px', fontFamily: T.fontBody }}>
              {/* Breadcrumb */}
              <div className="flex items-center gap-2 text-xs mb-6" style={{ color: T.muted }}>
                <button onClick={() => { setSelectedLezione(null); setLezioneData(null); }} className="hover:underline" style={{ color: T.violet }}>Corso</button>
                <span>/</span>
                <span>{lezioneData.modulo_titolo}</span>
                <span>/</span>
                <span style={{ color: T.text }}>{lezioneData.titolo}</span>
              </div>

              {/* Module progress bar */}
              {(() => {
                const m = moduli.find((x) => x.id === lezioneData.modulo_id);
                if (!m) return null;
                const mp = moduloProgress(m);
                return (
                  <div className="card mb-6 rounded-xl p-3">
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span style={{ color: T.muted }}>{m.titolo}</span>
                      <span style={{ color: T.violet }}>{mp}%</span>
                    </div>
                    <div className="w-full h-2 rounded-full" style={{ backgroundColor: T.border }}>
                      <div className="h-full rounded-full transition-all" style={{ width: `${mp}%`, background: T.gradPrimary }} />
                    </div>
                  </div>
                );
              })()}

              <h1 className="text-2xl font-bold mb-2" style={{ color: T.text, fontFamily: T.fontDisplay }}>{lezioneData.titolo}</h1>
              <div className="flex items-center gap-3 text-xs mb-6" style={{ color: T.muted }}>
                {lezioneData.completata && (
                  <span className="font-medium flex items-center gap-1" style={{ color: T.violet }}>
                    <CheckIcon size={16} /> Completata
                  </span>
                )}
              </div>

              {/* Content with copy protection */}
              <div className="prose-nv" style={{ userSelect: 'none' }} onContextMenu={(e) => e.preventDefault()}>
                <ReactMarkdown components={{
                  h1: ({children}) => <h1 className="text-2xl font-bold mt-6 mb-3" style={{ color: T.text, fontFamily: T.fontDisplay }}>{children}</h1>,
                  h2: ({children}) => <h2 className="text-xl font-bold mt-5 mb-2" style={{ color: T.text, fontFamily: T.fontDisplay }}>{children}</h2>,
                  h3: ({children}) => <h3 className="text-lg font-bold mt-4 mb-2" style={{ color: T.text, fontFamily: T.fontDisplay }}>{children}</h3>,
                  p: ({children}) => <p className="leading-relaxed mb-3" style={{ color: T.violet, fontFamily: T.fontBody }}>{children}</p>,
                  li: ({children}) => <li className="ml-4 list-disc mb-1" style={{ color: T.violet }}>{children}</li>,
                  strong: ({children}) => <strong className="font-semibold" style={{ color: T.text }}>{children}</strong>,
                  code: ({children}) => <code className="px-1.5 py-0.5 rounded text-sm" style={{ backgroundColor: T.border, color: T.violet, fontFamily: T.fontMono }}>{children}</code>,
                }}>{lezioneData.contenuto_markdown || ''}</ReactMarkdown>
              </div>

              {/* Complete button */}
              <div className="mt-10 mb-6">
                {lezioneData.completata ? (
                  <div className="rounded-xl px-6 py-4 text-sm font-medium flex items-center gap-2" style={{
                    backgroundColor: 'rgba(124,58,237,0.1)', border: '1px solid rgba(124,58,237,0.3)', color: T.violet,
                  }}>
                    <CheckIcon size={18} /> Lezione completata
                  </div>
                ) : (
                  <>
                    <button
                      ref={nextBtnRef}
                      onClick={() => {
                        if (!hasScrolledToBottom) return;
                        completaLezione();
                      }}
                      disabled={!hasScrolledToBottom || completing}
                      style={{
                        borderRadius: 9999, padding: '12px 24px', fontFamily: 'DM Sans', fontWeight: 700,
                        fontSize: '0.95rem', cursor: hasScrolledToBottom ? 'pointer' : 'not-allowed',
                        transition: 'all 0.4s ease', border: 'none', width: '100%',
                        background: hasScrolledToBottom ? 'linear-gradient(135deg,#7c3aed,#4f46e5)' : '#2d1b69',
                        color: hasScrolledToBottom ? 'white' : '#6b7280',
                        boxShadow: hasScrolledToBottom ? '0 4px 20px rgba(124,58,237,0.4)' : 'none',
                        animation: hasScrolledToBottom ? 'glow 2s ease-in-out infinite' : 'none',
                      }}
                    >
                      {completing ? 'Completamento...' : hasScrolledToBottom ? '✓ Ho letto, continua →' : '📖 Scorri per sbloccare'}
                    </button>
                    {!hasScrolledToBottom && (
                      <p style={{ textAlign: 'center', fontSize: '0.75rem', color: '#6b7280', marginTop: 8, fontFamily: 'DM Sans' }}>
                        👆 Leggi tutta la lezione per continuare
                      </p>
                    )}
                  </>
                )}
              </div>

              {/* Lesson Navigation */}
              <div className="lezione-nav" style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', gap: isMobile ? 10 : 16, paddingTop: 24, marginTop: 32, borderTop: `1px solid ${T.border}` }}>
                {prevLezione && !prevLezione.modulo_locked ? (
                  <button onClick={() => selectLezione(prevLezione.modulo_id, prevLezione.id)} style={{
                    background: T.elem, border: `1px solid ${T.border}`, borderRadius: 16, padding: '16px 24px',
                    display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4, cursor: 'pointer', transition: '0.2s', flex: 1, textAlign: 'left',
                  }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = T.violet; e.currentTarget.style.background = T.hover; e.currentTarget.style.transform = 'translateX(-2px)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.background = T.elem; e.currentTarget.style.transform = ''; }}>
                    <span style={{ fontFamily: T.fontBody, fontSize: '0.72rem', color: T.muted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>← Precedente</span>
                    <span style={{ fontFamily: T.fontBody, fontSize: '0.9rem', fontWeight: 600, color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 250 }}>{prevLezione.titolo}</span>
                  </button>
                ) : <div style={{ flex: 1 }} />}
                {nextLezione && !nextLezione.modulo_locked ? (
                  <button
                    onClick={() => {
                      if (!hasClickedHoLetto && !lezioneData.completata) return;
                      if (isMobile) { setShowConfermaModal(true); }
                      else { handleProssimaLezione(); }
                    }}
                    disabled={!hasClickedHoLetto && !lezioneData.completata}
                    style={{
                      flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'flex-end',
                      padding: '16px 20px', borderRadius: 16, transition: 'all 0.4s ease',
                      cursor: (hasClickedHoLetto || lezioneData.completata) ? 'pointer' : 'not-allowed',
                      border: `1px solid ${(hasClickedHoLetto || lezioneData.completata) ? '#7c3aed' : '#2d1b69'}`,
                      background: (hasClickedHoLetto || lezioneData.completata)
                        ? 'linear-gradient(135deg,rgba(124,58,237,0.2),rgba(79,70,229,0.1))'
                        : '#1c1640',
                      boxShadow: (hasClickedHoLetto || lezioneData.completata)
                        ? '0 0 20px rgba(124,58,237,0.3)' : 'none',
                      animation: (hasClickedHoLetto || lezioneData.completata) ? 'glow 2s ease-in-out infinite' : 'none',
                      textAlign: 'right',
                    }}
                  >
                    <span style={{
                      fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4,
                      color: (hasClickedHoLetto || lezioneData.completata) ? '#a78bfa' : '#6b7280',
                    }}>
                      {(hasClickedHoLetto || lezioneData.completata) ? 'Successivo →' : '🔒 Completa prima questa lezione'}
                    </span>
                    <span style={{
                      fontSize: '0.9rem', fontWeight: 600, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      color: (hasClickedHoLetto || lezioneData.completata) ? '#e2e8f0' : '#374151',
                    }}>
                      {nextLezione.titolo}
                    </span>
                  </button>
                ) : (!nextLezione && progress.percent === 100) ? (
                  <div style={{
                    background: T.gradPrimary, borderRadius: 16, padding: '16px 24px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, flex: 1, textAlign: 'center',
                  }}>
                    <span style={{ fontFamily: T.fontBody, fontSize: '0.95rem', fontWeight: 700, color: 'white' }}>🎉 Corso Completato!</span>
                  </div>
                ) : <div style={{ flex: 1 }} />}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Conferma Modal (mobile) */}
      {showConfermaModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(13,11,30,0.95)', zIndex: 9999,
          display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center', padding: 20,
        }}>
          <div style={{
            background: '#151230', border: '1px solid #2d1b69', borderRadius: 24,
            padding: '28px 24px', width: '100%', maxWidth: 420, textAlign: 'center',
          }}>
            <div style={{ fontSize: '2rem', marginBottom: 12 }}>📚</div>
            <h3 style={{ fontFamily: 'Syne', fontSize: '1.1rem', fontWeight: 800, color: '#e2e8f0', marginBottom: 8 }}>
              Hai letto tutta la lezione?
            </h3>
            <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: 24, fontFamily: 'DM Sans' }}>
              Assicurati di aver capito i concetti prima di continuare
            </p>
            <div style={{ display: 'flex', gap: 10, flexDirection: 'column' }}>
              <button
                onClick={() => { setShowConfermaModal(false); handleProssimaLezione(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                style={{
                  background: 'linear-gradient(135deg,#7c3aed,#4f46e5)', color: 'white', border: 'none', borderRadius: 9999,
                  padding: 14, fontFamily: 'DM Sans', fontWeight: 700, fontSize: '0.95rem', cursor: 'pointer', width: '100%',
                }}
              >
                ✅ Sì, vai alla prossima lezione
              </button>
              <button
                onClick={() => { setShowConfermaModal(false); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                style={{
                  background: 'transparent', color: '#a78bfa', border: '1px solid #2d1b69', borderRadius: 9999,
                  padding: 12, fontFamily: 'DM Sans', fontWeight: 600, fontSize: '0.88rem', cursor: 'pointer', width: '100%',
                }}
              >
                ↩ Torna a rileggere
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Modal */}
      {showResetModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(13,11,30,0.95)', backdropFilter: 'blur(8px)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 20, padding: 28, maxWidth: 400, width: '100%' }}>
            <h3 style={{ fontFamily: T.fontDisplay, fontSize: '1.2rem', fontWeight: 700, color: T.text, marginBottom: 12 }}>Sei sicuro?</h3>
            <p style={{ fontFamily: T.fontBody, fontSize: '0.88rem', color: T.muted, marginBottom: 24 }}>Il tuo progresso verrà azzerato completamente. Potrai rifare tutte le lezioni da capo.</p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowResetModal(false)} className="btn-secondary">Annulla</button>
              <button onClick={handleResetCorso} disabled={resetting} className="btn-danger" style={{ opacity: resetting ? 0.5 : 1 }}>
                {resetting ? 'Reset...' : 'Sì, ricomincia'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
